import React, { useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Grid,
  Divider,
  Stack,
  useTheme,
  Fade,
} from "@mui/material";
import { CheckCircle, Edit, Share } from "@mui/icons-material";
import { motion } from "framer-motion";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import { styled } from "@mui/material/styles";
import ShareIcon from "@mui/icons-material/Share";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RefreshIcon from "@mui/icons-material/Refresh";

const SuccessContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 1.5,
  position: "relative",
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  borderLeft: `5px solid ${theme.palette.success.main}`,
}));

const SuccessHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(3),
}));

const SuccessIcon = styled(CheckCircleIcon)(({ theme }) => ({
  fontSize: 40,
  color: theme.palette.success.main,
  marginRight: theme.spacing(2),
}));

const ActionButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1.25, 3),
  borderRadius: theme.shape.borderRadius * 1.5,
  fontWeight: 600,
  boxShadow: "none",
  textTransform: "none",
  fontSize: "0.95rem",
  transition: theme.transitions.create(["transform", "box-shadow"]),
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.1)",
  },
}));

/**
 * Formats field value for display based on field type
 *
 * @param {*} value Field value
 * @param {Object} field Field configuration
 * @returns {string|JSX.Element} Formatted field value
 */
const formatFieldValue = (value, field) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  if (field.componentType === "CheckboxGroup" && Array.isArray(value)) {
    return value.join(", ");
  }

  if (field.isPayment && field.name === "PaymentAmount" && value) {
    return `${field.currency || ""} ${parseFloat(value).toFixed(2)}`;
  }

  if (field.type === "date" && value) {
    try {
      return new Date(value).toLocaleDateString();
    } catch (e) {
      return value;
    }
  }

  if (
    field.componentType === "RadioGroup" ||
    field.componentType === "Select"
  ) {
    const option = field.options?.find((opt) => opt.value === value);
    return option ? option.label : value;
  }

  return value;
};

/**
 * Determines if a field should be shown in summary
 *
 * @param {Object} field Field configuration
 * @returns {boolean} Whether field should be shown
 */
const shouldShowField = (field) => {
  if (!field.name || field.isLabel) return false;
  if (field.hideInSummary) return false;

  // Skip some common fields that shouldn't be in summary
  const skipFields = ["ConfirmPassword", "Terms"];
  return !skipFields.includes(field.name);
};

/**
 * Displays form submission response with sharing and restart options
 *
 * @param {Object} props - Component props
 * @param {string} props.response - HTML response content to display
 * @param {Function} props.onReset - Function to reset the form
 * @param {boolean} props.showResetButton - Whether to show the reset button
 * @param {boolean} props.showShareButton - Whether to show the share button
 * @param {Function} props.onShare - Function to handle share action
 * @param {string} props.resetButtonText - Text for reset button
 * @param {boolean} props.enableAnotherRegistration - Whether "another registration" is enabled
 * @param {Array} props.featuresList - Array of enabled features
 */
const FormSummary = ({
  response,
  onReset,
  showResetButton = true,
  showShareButton = false,
  onShare,
  // Share Generator (the "create your I'm-attending card" flow). Independent of the legacy `showShareButton`
  // social-share flow: an event can have the Share Generator enabled while the legacy ShareButton feature is
  // off, in which case only the popup used to appear and the response page had no share entry point. When
  // enabled we render a persistent button here too, so dismissing the popup ("maybe later") no longer loses it.
  showShareGenerator = false,
  onShareGenerator,
  shareGeneratorLabel = "Share you're attending",
  resetButtonText = "Another registration?",
  enableAnotherRegistration = false,
  featuresList = [],
}) => {
  const responseBoxRef = useRef(null);
  const theme = useTheme();

  // Scroll to response when it changes
  useEffect(() => {
    if (response && responseBoxRef.current) {
      responseBoxRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [response]);

  if (!response) return null;

  // Determine response type for email verification
  const getResponseType = () => {
    if (!featuresList.includes("emailVerification")) {
      return {
        type: "success",
        title: "Registration Status",
        color: "success.main",
      };
    }

    // Safely convert response to string for analysis
    const responseText =
      typeof response === "string"
        ? response.toLowerCase()
        : String(response || "").toLowerCase();

    // if (
    //   responseText.includes("pending") ||
    //   responseText.includes("verify") ||
    //   responseText.includes("confirmation")
    // ) {
    //   return {
    //     type: "pending",
    //     title: "Verification Required",
    //     color: "warning.main",
    //   };
    // }

    // if (
    //   responseText.includes("reject") ||
    //   responseText.includes("denied") ||
    //   responseText.includes("error")
    // ) {
    //   return {
    //     type: "error",
    //     title: "Registration Issue",
    //     color: "error.main",
    //   };
    // }

    // if (
    //   responseText.includes("confirm") ||
    //   responseText.includes("accept") ||
    //   responseText.includes("success")
    // ) {
    //   return {
    //     type: "success",
    //     title: "Registration Successful",
    //     color: "success.main",
    //   };
    // }

    // Default to pending for email verification
    return {
      type: "success",
      title: "Registration Form Submitted",
      color: "info.main",
    };
  };

  const responseType = getResponseType();

  const getIcon = () => {
    switch (responseType.type) {
      case "success":
        return <CheckCircleIcon />;
      case "pending":
        return <AppRegistrationIcon />;
      case "error":
        return <RefreshIcon />;
      default:
        return <CheckCircleIcon />;
    }
  };

  return (
    <Fade in={true} timeout={500}>
      <SuccessContainer ref={responseBoxRef}>
        <SuccessHeader>
          <Box
            sx={{
              fontSize: 40,
              color: responseType.color,
              marginRight: theme.spacing(2),
              display: "flex",
              alignItems: "center",
            }}
          >
            {getIcon()}
          </Box>
          <Typography variant="h5" fontWeight={600} color={responseType.color}>
            {responseType.title}
          </Typography>
        </SuccessHeader>

        <div dangerouslySetInnerHTML={{ __html: response }} />

        {(enableAnotherRegistration || showShareGenerator) && (
          <>
            <Divider sx={{ my: 3 }} />

            <Stack direction="column" spacing={2}>
              {showShareGenerator && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <ActionButton
                    onClick={onShareGenerator}
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<ShareIcon />}
                  >
                    {shareGeneratorLabel}
                  </ActionButton>
                </Box>
              )}

              {enableAnotherRegistration && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <ActionButton
                    onClick={onReset}
                    variant="outlined"
                    color="primary"
                    size="large"
                    startIcon={<RefreshIcon />}
                  >
                    {resetButtonText}
                  </ActionButton>
                </Box>
              )}

              {/* The legacy "Share this event" button was REMOVED (owner 2026-08-15): the Share
                  Generator below supersedes it, and two share entry points on one response page
                  confused attendees. `showShareButton` / `onShare` are still accepted so the
                  existing `ShareButton` feature flag and callers keep working without error — the
                  legacy dialog simply no longer has a trigger here. */}
            </Stack>
          </>
        )}
      </SuccessContainer>
    </Fade>
  );
};

export default FormSummary;
